import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  TextField,
  Button,
  MenuItem,
  Avatar,
  Typography,
  Paper,
  Box,
  Container,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Toaster, toast } from "react-hot-toast";
import Swal from "sweetalert2";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import loginIllustration from "../images/dog.jpg"; // ภาพฝั่งซ้าย
import { useNavigate } from "react-router-dom";
import * as services from "./../services/services";

export default function CreateUser() {
  const navigate = useNavigate();

  const fetchHospital = useCallback(async () => {
    try {
      const res = await services.getHospitalList();
      setHospital(res.data.data);
    } catch (error) {
      console.error("Error fetching hospitals:", error);
    }
  }, []);

  useEffect(() => {
    fetchHospital();
  }, [fetchHospital]);

  const [form, setForm] = useState({
    username: "",
    password: "",
    rePassword: "",
    name: "",
    lastname: "",
    title: "",
    phoneNumber: "",
    idCard: "",
    license_no: "",
    image: "",
    hospitalId: "",
    email: "",
    roleId: 2,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [hospitals, setHospital] = useState([]);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      setImagePreview(URL.createObjectURL(file));

      try {
        const res = await services.uploadImage(file);
        const imageUrl = res.data;
        setForm({ ...form, image: imageUrl[0] });
      } catch (err) {
        toast.error("อัปโหลดรูปภาพไม่สำเร็จ");
      }

      return;
    }

    // field อื่นๆ
    setForm({ ...form, [name]: value });

    // Validation real-time
    if (name === "password") {
      if (value.length > 0 && value.length < 8) {
        setErrors((prev) => ({
          ...prev,
          password: "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
        }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }
    }

    if (name === "rePassword") {
      if (form.password !== value) {
        setErrors((prev) => ({ ...prev, rePassword: "รหัสผ่านไม่ตรงกัน" }));
      } else {
        setErrors((prev) => ({ ...prev, rePassword: "" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.username || !emailRegex.test(form.username)) {
      toast.error("กรุณากรอก Email ให้ถูกต้อง");
      return;
    }

    // Validate password
    if (form.password.length < 8) {
      toast.error("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
      return;
    }

    if (form.password !== form.rePassword) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return;
    }

    // Validate required fields
    if (!form.name.trim()) {
      toast.error("กรุณากรอกชื่อ");
      return;
    }

    if (!form.lastname.trim()) {
      toast.error("กรุณากรอกนามสกุล");
      return;
    }

    if (!form.hospitalId) {
      toast.error("กรุณาเลือกสังกัดโรงพยาบาล");
      return;
    }

    // Validate ID card (13 digits)
    if (form.idCard && !/^[0-9]{13}$/.test(form.idCard)) {
      toast.error("เลขบัตรประชาชนต้องเป็นตัวเลข 13 หลัก");
      return;
    }


    try {
      const res = await services.register(form);
      if (!res.data.success) throw new Error(res.data.error || "สมัครสมาชิกไม่สำเร็จ");

      Swal.fire({
        title: "สำเร็จ!",
        text: "ท่านสมัครใช้งานสำเร็จ กรุณารอเจ้าหน้าที่โรงพยาบาลอนุมัติก่อนเข้าใช้งาน",
        icon: "success",
        confirmButtonText: "ตกลง",
        customClass: {
          container: "my-swal",
        },
      }).then(() => {
        navigate("/auth/login");
      });
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      if (status === 409) {
        toast.error("อีเมลนี้ถูกใช้งานแล้ว กรุณาใช้อีเมลอื่น");
      } else if (status === 400) {
        const msg = error?.response?.data?.error || "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง";
        toast.error(msg);
      } else if (status === 500) {
        toast.error("เซิร์ฟเวอร์ขัดข้อง กรุณาลองใหม่ภายหลัง");
      } else {
        toast.error(error?.message || "สมัครสมาชิกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      }
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <Toaster />

      {/* ฝั่งซ้าย (ภาพประกอบ) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width: "40%",
          backgroundImage: `url(${loginIllustration})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.2)",
          },
        }}
      />

      {/* ฝั่งขวา (ฟอร์ม) */}
      <Box
        sx={{
          display: "flex",
          width: { xs: "100%", md: "60%" },
          justifyContent: "center",
          alignItems: "center",
          p: 4,
          overflowY: "auto",
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 6 },
              borderRadius: 4,
              bgcolor: "white",
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  background:
                    "linear-gradient(90deg, #c2451f 0%, #F57C00 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                ลงทะเบียนบุคลากร
              </Typography>

              {/* Avatar Upload */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  items: "center",
                  mb: 2,
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="image-upload"
                  type="file"
                  name="image"
                  onChange={handleChange}
                />
                <label
                  htmlFor="image-upload"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    src={imagePreview}
                    sx={{
                      width: 100,
                      height: 100,
                      mb: 1,
                      border: "3px solid #F57C00",
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="center"
                  >
                    คลิกเพื่ออัปโหลดรูปโปรไฟล์
                  </Typography>
                </label>
              </Box>

              {/* Form Fields */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Email"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Password (8 ตัวขึ้นไป)"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                            onClick={() => setShowPassword((prev) => !prev)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Re-Password"
                    type={showRePassword ? "text" : "password"}
                    name="rePassword"
                    value={form.rePassword}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    error={!!errors.rePassword}
                    helperText={errors.rePassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label={showRePassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                            onClick={() => setShowRePassword((prev) => !prev)}
                          >
                            {showRePassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="คำนำหน้า"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="ชื่อ"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="นามสกุล"
                    name="lastname"
                    value={form.lastname}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
                {/* <Grid item xs={12} sm={12}>
                  <TextField
                    label="อีเมล"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    fullWidth
                    required
                    size="small"
                    type="email"
                  />
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="เบอร์โทรศัพท์"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="เลขบัตรประชาชน"
                    name="idCard"
                    value={form.idCard}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="เลขที่ใบอนุญาต"
                    name="license_no"
                    value={form.license_no}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="สังกัดโรงพยาบาล"
                    name="hospitalId"
                    value={form.hospitalId}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  >
                    {hospitals.map((hospital) => (
                      <MenuItem key={hospital.id} value={hospital.id}>
                        {hospital.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {/* Submit Button */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    borderRadius: 2,
                    background:
                      "linear-gradient(90deg, #c2451f 0%, #F57C00 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #a03a1a 0%, #d86d00 100%)",
                    },
                  }}
                >
                  สมัครใช้งาน
                </Button>
              </Box>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  มีบัญชีอยู่แล้ว?{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    color="primary"
                    sx={{ cursor: "pointer", fontWeight: "bold" }}
                    onClick={() => navigate("/auth/login")}
                  >
                    เข้าสู่ระบบ
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
